import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    FormControl,
    InputAdornment,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { Iconify } from 'src/components';
import useDemoMode, { buildPracticeNameMasker } from 'src/hooks/useDemoMode';

export default function AdsReportingPracticeSelector({ practices = [], value = null, onChange, hidden = false }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [demoMode] = useDemoMode();
    const maskName = useMemo(() => buildPracticeNameMasker(practices, demoMode), [demoMode, practices]);
    const filteredPractices = useMemo(() => {
        if (!searchQuery || demoMode) return practices;
        const query = searchQuery.toLowerCase();
        return practices.filter((practice) => practice?.name?.toLowerCase().includes(query));
    }, [demoMode, practices, searchQuery]);

    if (hidden || practices.length <= 1) return null;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Box
                sx={{
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: '9px',
                    bgcolor: '#e0f2fe',
                    color: '#0369a1',
                }}
            >
                <Iconify icon="mdi:office-building" width={21} height={21} />
            </Box>
            <FormControl size="small" sx={{ minWidth: { xs: 190, sm: 230 } }}>
                <InputLabel id="ads-reporting-practice-label">Reporting practice</InputLabel>
                <Select
                    labelId="ads-reporting-practice-label"
                    value={value || ''}
                    label="Reporting practice"
                    input={<OutlinedInput label="Reporting practice" />}
                    onChange={(event) => onChange(Number(event.target.value))}
                    onClose={() => setSearchQuery('')}
                    MenuProps={{ PaperProps: { style: { maxHeight: 350 } }, autoFocus: false }}
                    renderValue={(selected) => {
                        const selectedPractice = practices.find((practice) => Number(practice.id) === Number(selected));
                        return maskName(selectedPractice) || 'Select practice';
                    }}
                >
                    <Box sx={{ px: 1.5, py: 1, position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search practices..."
                            value={searchQuery}
                            disabled={demoMode}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            onKeyDown={(event) => event.stopPropagation()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="eva:search-fill" width={18} sx={{ color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    {filteredPractices.map((practice) => (
                        <MenuItem key={practice.id} value={Number(practice.id)}>
                            <ListItemText primary={maskName(practice)} />
                        </MenuItem>
                    ))}
                    {filteredPractices.length === 0 && searchQuery && (
                        <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>No practices found</Typography>
                        </Box>
                    )}
                </Select>
            </FormControl>
        </Box>
    );
}

AdsReportingPracticeSelector.propTypes = {
    practices: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
    })),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    hidden: PropTypes.bool,
};
